import React, { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Shield, AlertTriangle, CheckCircle2 } from "lucide-react";
import { projects } from "../data/mock";

const Projects = () => {
  const [scanningProject, setScanningProject] = useState(null);

  const handleScanHover = (projectId) => {
    setScanningProject(projectId);
    setTimeout(() => setScanningProject(null), 2000);
  };

  return (
    <section
      id="projects"
      className="grid-background"
      style={{
        padding: "100px 7.6923%",
        background: "var(--bg-primary)",
        borderTop: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: "center",
            marginBottom: "80px",
          }}
        >
          <h2
            style={{
              fontSize: "48px",
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: "16px",
            }}
          >
            Featured Projects
          </h2>
          <p style={{ fontSize: "18px", color: "var(--text-muted)" }}>
            Hover to trigger security scan simulation
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
            gap: "40px",
          }}
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -6, scale: 1.01 }}
              onMouseEnter={() => handleScanHover(project.id)}
              className="glass hover-lift"
              style={{
                padding: "30px",
                borderRadius: "0px",
                border: scanningProject === project.id ? "1px solid var(--brand-primary)" : "1px solid var(--border-medium)",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s ease",
              }}
            >
              {/* Scanning Overlay - Simpler Effect */}
              {scanningProject === project.id && (
                <motion.div
                  initial={{ y: "-100%" }}
                  animate={{ y: "200%" }}
                  transition={{ duration: 2, ease: "linear" }}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: "var(--brand-primary)",
                    zIndex: 2,
                    pointerEvents: "none",
                    opacity: 0.6,
                  }}
                />
              )}

              {/* Content */}
              <div style={{ position: "relative", zIndex: 2 }}>
                {/* Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "20px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "24px",
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      flex: 1,
                    }}
                  >
                    {project.title}
                  </h3>
                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        height: "40px",
                        background: "rgba(0, 255, 209, 0.1)",
                        borderRadius: "0px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <ExternalLink size={20} color="var(--brand-primary)" />
                    </a>
                  )}
                </div>

                {/* Company */}
                <div
                  style={{
                    fontSize: "14px",
                    color: "var(--brand-primary)",
                    marginBottom: "16px",
                    fontWeight: 500,
                  }}
                >
                  {project.company}
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: "16px",
                    color: "var(--text-secondary)",
                    lineHeight: "1.6",
                    marginBottom: "20px",
                  }}
                >
                  {project.description}
                </p>

                {/* Technologies */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "20px",
                  }}
                >
                  {project.technologies.map((tech, i) => (
                    <span
                      key={i}
                      style={{
                        padding: "6px 12px",
                        background: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "0px",
                        fontSize: "12px",
                        color: "var(--text-secondary)",
                        border: "1px solid var(--border-subtle)",
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                {/* Impact */}
                <div
                  style={{
                    padding: "12px",
                    background: "rgba(0, 255, 136, 0.05)",
                    borderRadius: "0px",
                    border: "1px solid rgba(0, 255, 136, 0.2)",
                    marginBottom: "20px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "var(--success)",
                      fontWeight: 500,
                    }}
                  >
                    💡 {project.impact}
                  </span>
                </div>

                {/* Security Status */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px",
                    background: scanningProject === project.id ? "rgba(0, 255, 209, 0.05)" : "rgba(255, 255, 255, 0.02)",
                    borderRadius: "0px",
                    border: "1px solid var(--border-subtle)",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {scanningProject === project.id ? (
                      <>
                        <Shield size={20} color="var(--brand-primary)" className="rotating" />
                        <span style={{ fontSize: "14px", color: "var(--brand-primary)" }}>Scanning...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={20} color="var(--success)" />
                        <span style={{ fontSize: "14px", color: "var(--success)" }}>{project.scanStatus}</span>
                      </>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
                    <span style={{ color: "var(--text-muted)" }}>
                      <AlertTriangle size={14} style={{ display: "inline", marginRight: "4px" }} />
                      {project.vulnerabilities} vulnerabilities
                    </span>
                    <span style={{ color: "var(--brand-primary)", fontWeight: 600 }}>Score: {project.securityScore}/100</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>
        {`
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .rotating {
            animation: rotate 2s linear infinite;
          }
        `}
      </style>
    </section>
  );
};

export default Projects;
